import env from 'process.env'
import { AmplitudeClient, Callback, Config, Identify } from 'amplitude-js'
import Logger from '../../../utils/logger'
import { load } from '../../../utils'
import { AMPLITUDE_INSTANCE_KEY, AMPLITUDE_LIBRARY_URL } from '../../../constants'

type PlainObject = Record<string, unknown>

const options = {
  includeGclid: true,
  includeUtm: true,
  includeReferrer: true,
  logAttributionCapturedEvent: true
} as Config

const {
  AMPLITUDE_API_KEY
} = env

type AmplitudeLibrary = {
  init(apiKey: string, userId?: string, config?: Config, callback?: (client: AmplitudeClient) => void): void,
  getInstance(instanceName?: string): AmplitudeClient
  getSessionId: () => number,
  setUserId: (userId: string | null) => AmplitudeClient,
  identify: (identify: Identify) => AmplitudeClient,
  logEventWithTimestamp<T>(event: string, data?: T, timestamp?: number, callback?: Callback, ): void
}

export class AmplitudeAnalytics {
  #enabled = true
  #amplitude!: AmplitudeLibrary

  get sessionId(): number | undefined {
    return this.amplitude.getInstance().getSessionId()
  }

  get name(): string {
    return 'AMPLITUDE'
  }

  get amplitude() {
    return this.#amplitude
  }

  set userId(userId: string | null) {
    this.amplitude.getInstance().setUserId(userId)
  }

  set enabled(enabled: boolean) {
    this.#enabled = enabled
  }

  async init(userId?: string | undefined, key?: string): Promise<void | AmplitudeClient> {
    if (!this.#amplitude) {
      const oldAmplitude = window.amplitude ? Object.assign(Object.create(Object.getPrototypeOf(window.amplitude)), window.amplitude) : undefined

      await load(AMPLITUDE_LIBRARY_URL, key ?? AMPLITUDE_INSTANCE_KEY)

      this.#amplitude = Object.assign(Object.create(Object.getPrototypeOf(window.amplitude)), window.amplitude) as unknown as AmplitudeLibrary

      //@ts-expect-error: ignore
      if (key) window[key] = this.#amplitude
      if (oldAmplitude) window.amplitude = oldAmplitude

      Logger.BLACK.OUTLINE('amplitude', 'init', this.amplitude)
    }

    return new Promise<void | AmplitudeClient>(resolve => {
      this.#amplitude.init(AMPLITUDE_API_KEY, userId, options, resolve)
    })
  }

  async identify(identify: Identify): Promise<void | AmplitudeClient> {
    this.amplitude.getInstance().identify(identify)
  }


  async logEvent(event: string, data?: PlainObject, timestamp: number = Date.now()): Promise<void | AmplitudeClient> {
    return new Promise<void | AmplitudeClient>(resolve => {
      if (!this.#enabled || !this.amplitude) return resolve()

      try {
        const payload = { ...(data || {}) }

        Logger.ORANGE.OUTLINE(this.name, `[${event}]`, payload)

        this.amplitude.getInstance().logEventWithTimestamp(event, payload, timestamp, resolve as never)
      }
      catch (error) {
        Logger.ERROR(`${this.name}:[sendEvent]`, error)
        resolve()
      }
    })
  }
}

export default () => AmplitudeAnalytics
